import { NgZone, Provider } from '@angular/core';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { environment } from '../../environments/environment';
import { FirebaseApp, initializeApp } from '../core/firebase/app';
import { Auth, connectAuthEmulator, getAuth } from '../core/firebase/auth';
import {
  Analytics,
  getAnalytics,
  initializeAnalytics,
} from '../core/firebase/analytics';
import {
  EMBED_COOKIE_FLAGS,
  UNKNOWN_PARTNER,
  embedHost,
  isInIframe,
} from '../core/embed-analytics';
import { Ga4Param } from '../core/ga4-events';
import { INTERNAL_TRAFFIC, isInternalDevice } from '../core/internal-traffic';
import {
  Storage,
  connectStorageEmulator,
  getStorage,
} from '../core/firebase/storage';

/**
 * App Check must be active before the other services make requests, so the
 * app factory activates it right after initializing the app. App Check and
 * Analytics run outside the Angular zone: their background timers must not
 * trigger change detection.
 *
 * Exception: inside a customer's iframe App Check starts once the page is idle.
 * It loads reCAPTCHA (several hundred KB plus a Google iframe), and embeds never
 * use the Firebase services it protects on first render: they read the public
 * REST API, which does not check App Check tokens.
 */
export const firebaseProviders: Provider[] = [
  {
    provide: FirebaseApp,
    deps: [NgZone],
    useFactory: (zone: NgZone) => {
      const app = initializeApp(environment.firebase);
      zone.runOutsideAngular(() => {
        if (environment.name === 'dev') {
          (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN =
            environment.appCheckSiteKey;
        }
        const activateAppCheck = () =>
          initializeAppCheck(app, {
            provider: new ReCaptchaV3Provider(environment.appCheckSiteKey),
            isTokenAutoRefreshEnabled: true,
          });
        if (isInIframe()) {
          whenIdleAfterLoad(activateAppCheck);
        } else {
          activateAppCheck();
        }
      });
      return app;
    },
  },
  {
    provide: Auth,
    deps: [FirebaseApp],
    useFactory: (app: FirebaseApp) => {
      const auth = getAuth(app);
      if (environment.useEmulators) {
        connectAuthEmulator(auth, 'http://localhost:9099', {
          disableWarnings: true,
        });
      }
      return auth;
    },
  },
  {
    provide: Storage,
    deps: [FirebaseApp],
    useFactory: (app: FirebaseApp) => {
      const storage = getStorage(app);
      if (environment.useEmulators) {
        connectStorageEmulator(storage, 'localhost', 9199);
      }
      return storage;
    },
  },
  {
    provide: Analytics,
    deps: [FirebaseApp, NgZone],
    useFactory: (app: FirebaseApp, zone: NgZone) =>
      zone.runOutsideAngular(() => {
        // Config parameters go with every event of this stream, including the
        // first page_view (a later `set` would miss it). See embed-analytics
        // and internal-traffic.
        const internal = isInternalDevice() ? INTERNAL_TRAFFIC : {};
        if (!isInIframe()) {
          return isInternalDevice()
            ? initializeAnalytics(app, { config: internal })
            : getAnalytics(app);
        }
        return initializeAnalytics(app, {
          config: {
            cookie_flags: EMBED_COOKIE_FLAGS,
            [Ga4Param.partnerWebsite]: embedHost() ?? UNKNOWN_PARTNER,
            ...internal,
          },
        });
      }),
  },
];

/** Runs `fn` once the page has loaded and the browser is idle (max. 5 s later). */
function whenIdleAfterLoad(fn: () => void): void {
  const schedule = () =>
    'requestIdleCallback' in window
      ? window.requestIdleCallback(() => fn(), { timeout: 5000 })
      : setTimeout(fn, 1000);
  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }
}
